import api from '@/utils/http'
import { buildPivotSheet } from './reportDataExport'
import { fetchBasicMain, fetchBehaviorDetail } from './reportDataApi'
import { buildStayTimeSql, buildTraceStayTimeSql } from './projectExport/projectExportSqlBuilders'

export function buildDateList(start, end) {
  const list = []
  const cursor = new Date(start)
  const endDate = new Date(end)
  while (cursor <= endDate) {
    list.push(cursor.toISOString().slice(0, 10))
    cursor.setDate(cursor.getDate() + 1)
  }
  return list
}

export async function runExportFromConfig(config, { project, startDate, endDate, ctrl }) {
  const exportForm = config.exportForm || {}
  const exportSheets = config.exportSheets || []
  const dates = buildDateList(startDate, endDate)
  const sheets = []

  for (const sheetConfig of exportSheets) {
    if (sheetConfig.category === 'traffic') {
      sheets.push(await buildTrafficSheet(sheetConfig, dates, exportForm, project, ctrl))
    } else {
      sheets.push(await buildBehaviorSheet(sheetConfig, dates, exportForm, project, ctrl))
    }
  }
  ctrl?.stepDone('所有 Sheet 数据查询完成', '汇总')
  return sheets
}

async function buildTrafficSheet(sheetConfig, dates, exportForm, project, ctrl) {
  const channelConfigs = sheetConfig.channelConfigs || [{ channel: '__overall__', metrics: ['pv', 'uv'] }]
  const allMetricKeys = [...new Set(channelConfigs.flatMap((c) => c.metrics || []))]
  const valueMap = {}
  const items = []

  for (const cfg of channelConfigs) {
    const { channel, metrics: channelMetrics } = cfg
    const label = channel === '__overall__' ? '整体' : channel
    items.push(label)
    const stayMetrics = channelMetrics.filter((m) => ['sumStayTime', 'avgStayTime'].includes(m))
    const normalMetrics = channelMetrics.filter((m) => !['sumStayTime', 'avgStayTime'].includes(m))

    ctrl?.report(`[接口] ${sheetConfig.sheetName} · ${label} → 查询流量`, { label: `${sheetConfig.sheetName} / ${label}` })
    const params = { projectId: project.id, startTime: dates[0], endTime: dates[dates.length - 1] }
    if (channel !== '__overall__') params.channel = channel
    const result = await fetchBasicMain(params)

    valueMap[label] = {}
    dates.forEach((d) => {
      valueMap[label][d] = {}
      allMetricKeys.forEach((m) => { valueMap[label][d][m] = 0 })
    })
    ;(result?.dailyBasics || []).forEach((item) => {
      normalMetrics.forEach((m) => {
        if (item[m] !== undefined) valueMap[label][item.date][m] = Number(item[m]) || 0
      })
    })

    if (stayMetrics.length) {
      ctrl?.report(`[DataWorks] ${sheetConfig.sheetName} · ${label} → 停留时长`, { label: `${sheetConfig.sheetName} / ${label}` })
      const byChannel = channel !== '__overall__'
      const sql = exportForm.stayTimeSource === 'traceId'
        ? buildTraceStayTimeSql({
          projectCode: project.code,
          version: project.version,
          startDate: dates[0],
          endDate: dates[dates.length - 1],
          mode: 'daily',
          byChannel,
          stayAvgBase: exportForm.stayAvgBase
        })
        : buildStayTimeSql({
          projectCode: project.code,
          version: project.version,
          startDate: dates[0],
          endDate: dates[dates.length - 1],
          mode: 'daily',
          byChannel,
          stayAvgBase: exportForm.stayAvgBase
        })
      const stayResult = await api.executeDataWorksSql(sql)
      ;(stayResult?.data || []).forEach((row) => {
        const d = row.time_scope
        if (d && valueMap[label]?.[d]) {
          if (stayMetrics.includes('sumStayTime')) valueMap[label][d].sumStayTime = Number(row.totalTimeOnSite || row.timeOnSite || 0)
          if (stayMetrics.includes('avgStayTime')) valueMap[label][d].avgStayTime = Number(row.avg_stay_seconds || 0)
        }
      })
    }
    ctrl?.stepDone(`已完成：${sheetConfig.sheetName} / ${label}`, `${sheetConfig.sheetName} / ${label}`)
  }

  ctrl?.report(`正在组装 Sheet：${sheetConfig.sheetName}`, { label: sheetConfig.sheetName })
  const sheet = buildPivotSheet({
    sheetName: sheetConfig.sheetName,
    orientation: exportForm.orientation || 'date-row',
    dateList: dates,
    metrics: allMetricKeys,
    items,
    valueMap,
    themeKey: exportForm.themeKey || 'ocean',
    includeSumRow: exportForm.includeSummaryRow && !exportForm.summaryDedup && !exportForm.summaryCumDaily || false,
    includeDailySumRow: exportForm.includeSummaryRow && exportForm.summaryCumDaily || false,
    includeDedupRow: exportForm.includeSummaryRow && exportForm.summaryDedup || false,
    noCumDailyKeys: ['sumStayTime', 'avgStayTime'],
    itemMetrics: Object.fromEntries(
      channelConfigs.map((cfg) => [
        cfg.channel === '__overall__' ? '整体' : cfg.channel,
        cfg.metrics || []
      ])
    )
  })
  ctrl?.stepDone(`Sheet 已生成：${sheetConfig.sheetName}`, sheetConfig.sheetName)
  return sheet
}

async function buildBehaviorSheet(sheetConfig, dates, exportForm, project, ctrl) {
  const items = []
  const valueMap = {}
  const targets = sheetConfig.channels?.length ? sheetConfig.channels : ['__overall__']
  const mergedGroups = (sheetConfig.mergedBehaviors || []).filter((m) => m.behaviors?.length)

  for (const target of targets) {
    const channelLabel = target === '__overall__' ? '整体' : target
    for (const behaviorName of sheetConfig.behaviors || []) {
      const itemLabel = targets.length > 1 ? `${channelLabel} / ${behaviorName}` : behaviorName
      items.push(itemLabel)
      valueMap[itemLabel] = {}
      dates.forEach((d) => { valueMap[itemLabel][d] = { pv: 0, uv: 0 } })
      ctrl?.report(`[接口] ${sheetConfig.sheetName} · ${behaviorName}`, { label: itemLabel })
      const result = await fetchBehaviorDetail({
        projectId: project.id,
        startTime: dates[0],
        endTime: dates[dates.length - 1],
        indexName: behaviorName,
        channel: target === '__overall__' ? '' : target
      })
      ;(result?.dailyCustoms || []).forEach((item) => {
        valueMap[itemLabel][item.date] = { pv: Number(item.pv) || 0, uv: Number(item.uv) || 0 }
      })
      ctrl?.stepDone(`已完成：${itemLabel}`, itemLabel)
    }
  }

  for (const merged of mergedGroups) {
    const mergeLabel = merged.name || merged.behaviors.join('+')
    if (merged.keepOriginal) {
      for (const target of targets) {
        const channelLabel = target === '__overall__' ? '整体' : target
        for (const bName of merged.behaviors || []) {
          const itemLabel = targets.length > 1 ? `${channelLabel} / ${bName}` : bName
          if (!items.includes(itemLabel)) {
            items.push(itemLabel)
            valueMap[itemLabel] = {}
            dates.forEach((d) => { valueMap[itemLabel][d] = { pv: 0, uv: 0 } })
            ctrl?.report(`[接口] ${sheetConfig.sheetName} · 组内「${bName}」`, { label: itemLabel })
            const result = await fetchBehaviorDetail({
              projectId: project.id,
              startTime: dates[0],
              endTime: dates[dates.length - 1],
              indexName: bName,
              channel: target === '__overall__' ? '' : target
            })
            ;(result?.dailyCustoms || []).forEach((item) => {
              valueMap[itemLabel][item.date] = { pv: Number(item.pv) || 0, uv: Number(item.uv) || 0 }
            })
            ctrl?.stepDone(`已完成：${itemLabel}`, itemLabel)
          }
        }
      }
    }
    items.push(mergeLabel)
    valueMap[mergeLabel] = {}
    dates.forEach((d) => { valueMap[mergeLabel][d] = { pv: 0, uv: 0 } })
    ctrl?.report(`[DataWorks] ${sheetConfig.sheetName} · 合并「${mergeLabel}」`, { label: mergeLabel })
    const inList = merged.behaviors.map((b) => `'${String(b).replace(/'/g, "''")}'`).join(',')
    const nextDay = new Date(dates[dates.length - 1])
    nextDay.setDate(nextDay.getDate() + 1)
    const nextDayStr = nextDay.toISOString().slice(0, 10)
    const sql = `SELECT TO_CHAR(FROM_UNIXTIME(t_timestamp/1000),'yyyy-mm-dd') AS time_scope, COUNT(user_id) AS pv, COUNT(DISTINCT user_id) AS uv FROM mf_tracking_data_origin_record WHERE project_code='${String(project.code).replace(/'/g, "''")}' AND custom_type IN (${inList}) AND t_timestamp>=TO_MILLIS(DATETIME '${dates[0]} 00:00:00') AND t_timestamp<TO_MILLIS(DATETIME '${nextDayStr} 00:00:00') GROUP BY time_scope`
    const result = await api.executeDataWorksSql(sql)
    ;(result?.data || []).forEach((row) => {
      if (row.time_scope && valueMap[mergeLabel]?.[row.time_scope]) {
        valueMap[mergeLabel][row.time_scope] = { pv: Number(row.pv) || 0, uv: Number(row.uv) || 0 }
      }
    })
    ctrl?.stepDone(`已完成：${mergeLabel}`, mergeLabel)
  }

  const sheet = buildPivotSheet({
    sheetName: sheetConfig.sheetName,
    orientation: exportForm.orientation || 'date-row',
    dateList: dates,
    metrics: ['pv', 'uv'],
    items,
    valueMap,
    themeKey: exportForm.themeKey || 'ocean',
    includeSumRow: exportForm.includeSummaryRow || false,
    includeDailySumRow: exportForm.includeSummaryRow && exportForm.summaryCumDaily || false,
    includeDedupRow: exportForm.includeSummaryRow && exportForm.summaryDedup || false,
    noCumDailyKeys: []
  })
  ctrl?.stepDone(`Sheet 已生成：${sheetConfig.sheetName}`, sheetConfig.sheetName)
  return sheet
}
